import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackDropLoader from '../components/BackDropLoader';
import { getPersonalTrainingCustomTrainingWorkouts, createWorkoutDetails } from '../features/trainingSession/services/trainingSessionService';
import TrainingSetModal from '../features/trainingSession/components/TrainingSetModal';
import TrainingWorkoutInfoModal from '../features/trainingSession/components/TrainingWorkoutInfoModal';
import { TrainingSessionDetailMobileBlock } from '../features/trainingSession/components/TrainingSessionDetailMobileBlock';
import Table from '../components/Table/Table';
import { Typography, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react';
import { useAuth } from '../contexts/authContext';
import { useTheme  } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

export default function TrainingSessionDetailPage() {
  const { id: trainingId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [workoutsData, setWorkoutsData] = useState([]);
  const [error, setError] = useState('');
  const auth = useAuth();
  const [trainingModalOpen, setTrainingModalOpen] = useState(false);
  const [currentSetsCount, setCurrentSetsCount] = useState(0);
  const [currentWorkoutId, setWorkoutId] = useState(0);
  const [trainingInfoModalOpen, setTrainingInfoModalOpen] = useState(false);
  const [selectedWorkoutDetails, setSelectedWorkoutDetails] = useState([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchWorkoutsData = async () => {
    try {
      const data = await getPersonalTrainingCustomTrainingWorkouts(trainingId, auth.user.id);
      const sortedData = data.sort((a, b) => a.workoutOrder - b.workoutOrder);
      setWorkoutsData(sortedData);
      setLoading(false);
    } catch (error) {
      setError('Failed to load workouts data');
      setLoading(false);
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchWorkoutsData();
  }, [trainingId, enqueueSnackbar, auth.user.id]);

  const isWorkoutEdited = (workout) => workout.isUpdated;

  const handleRowClick = (rowData) => {
    setCurrentSetsCount(rowData.sets || 0);
    setWorkoutId(rowData.id);
    setTrainingModalOpen(true);
  };

  const handleSaveSets = async (setsData) => {
    try {
      setLoading(true);
      await createWorkoutDetails(setsData);

      enqueueSnackbar('Workout details saved successfully', { variant: 'success' });
      
      setTrainingModalOpen(false);
      await fetchWorkoutsData();
      setLoading(false);
    } catch (error) {
      enqueueSnackbar('Failed to save workout details', { variant: 'error' });
    }
  };

  const handleTrainingInfoRowClick = (rowData) => {
    setSelectedWorkoutDetails(rowData.workoutDetails || []);
    setTrainingInfoModalOpen(true);
  };

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => navigate('/dashboard/training-session')}
        style={{ marginBottom: '20px' }}
      >
        Back to Training Sessions
      </Button>
      <BackDropLoader open={loading} />
      {error && <Typography color="error">{error}</Typography>}
      {!loading && (
        isSmallScreen ? (
          <TrainingSessionDetailMobileBlock
            workoutsData={workoutsData}
            onRowClick={handleRowClick}
            onTrainingInfoClick={handleTrainingInfoRowClick}
          />
        ) : (
          <Table
            title="Workout Details"
            data={workoutsData}
            onRowClick={(event, rowData) => !isWorkoutEdited(rowData) && handleRowClick(rowData)}
            columns={[
              { title: 'Workout Name', field: 'workoutName' },
              { title: 'Category', field: 'categoryName' },
              { title: 'Time (minutes)', field: 'timeMinutes', render: rowData => rowData.timeMinutes > 0 ? rowData.timeMinutes : '-' },
              { title: 'Calories', field: 'calories', render: rowData => rowData.calories > 0 ? rowData.calories : '-' },
              { title: 'Distance (km)', field: 'distanceKm', render: rowData => rowData.distanceKm > 0 ? rowData.distanceKm : '-' },
              { title: 'Sets', field: 'sets', render: rowData => rowData.sets > 0 ? rowData.sets : '-' },
              { title: 'Repetitions', field: 'repetitions', render: rowData => rowData.repetitions > 0 ? rowData.repetitions : '-' },
              {
                title: 'Info',
                render: rowData => (
                  <Icon
                    icon={isWorkoutEdited(rowData) ? 'carbon:view-filled' : 'carbon:view-filled'}
                    onClick={() => handleTrainingInfoRowClick(rowData)}
                    style={{
                      marginLeft: '10px',
                      width: '24px',
                      height: '24px',
                      color: isWorkoutEdited(rowData) ? 'green' : 'gray',
                      pointerEvents: isWorkoutEdited(rowData) ? 'auto' : 'none',
                    }}
                  />
                ),
              },
              {
                title: 'Status',
                render: rowData => (
                  <Icon
                    icon={isWorkoutEdited(rowData) ? 'material-symbols:check-circle' : 'material-symbols:check-circle'}
                    style={{
                      marginLeft: '10px',
                      width: '24px',
                      height: '24px',
                      color: isWorkoutEdited(rowData) ? 'green' : 'gray',
                    }}
                  />
                ),
              },
            ]}
            detailPanel={(rowData) => {
              return (
                <div style={{ padding: '20px', backgroundColor: '#f5f5f5' }}>
                  {!isWorkoutEdited(rowData) ? (
                    <Typography>
                      Add workout info by clicking on the row
                    </Typography>
                  ) : (
                    <Typography>
                      Workout info already recorded
                    </Typography>
                  )}
                </div>
              );
            }}
            isLoading={loading}
            options={{
              rowStyle: rowData => ({
                backgroundColor: isWorkoutEdited(rowData) ? '#f5f5f5' : 'white',
                pointerEvents: isWorkoutEdited(rowData) ? 'none' : 'auto',
              }),
            }}
          />
        )
      )}
      <TrainingWorkoutInfoModal
        isOpen={trainingInfoModalOpen}
        onClose={() => setTrainingInfoModalOpen(false)}
        workoutDetails={selectedWorkoutDetails}
      />
      
      <TrainingSetModal
        isOpen={trainingModalOpen}
        onClose={() => setTrainingModalOpen(false)}
        onSave={handleSaveSets}
        setsCount={currentSetsCount}
        workoutId={currentWorkoutId}
        trainingId={trainingId}
        userId={auth.user.id}
      />
    </div>
  );
}
